/**
 * The official forms the Action Plan fills in — SA485, SA457, SA456 and the ATO's NAT 75073 — and the
 * answers it writes on each (owner, 3 Oct 2026: "all the official forms").
 *
 * Each form is the government's own PDF (src/assets/forms, mapped by tools/forms/map_official_form.py)
 * plus a function that turns the profile into values for its mapped boxes. Only what the profile
 * knows is written — names, dates of birth, the home and its loan, who owns it, who lives there —
 * and nothing is guessed: no reference numbers, addresses, legal descriptions or signatures.
 */
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { fillOfficialForm, fillAcroForm, openFilledPdf, type FormTemplate, type FormValues } from './officialFormFill.ts';
import { type Who, num, isCouple, personOf, otherOf, sharesFor, homeLoanBalance, loanBalanceFor, ownsHome, completerOf, assetsOf, giftsInLastFiveYears } from './profileFacts.ts';
import type { PlanFacts } from './planPosition.ts';
import sa485Template from '../../assets/forms/sa485-2607.json';
import sa457Template from '../../assets/forms/sa457-2604.json';
import sa456Template from '../../assets/forms/sa456-2511.json';

const SA485_PDF = new URL('../../assets/forms/sa485-2607.pdf', import.meta.url).href;
const SA457_PDF = new URL('../../assets/forms/sa457-2604.pdf', import.meta.url).href;
const SA456_PDF = new URL('../../assets/forms/sa456-2511.pdf', import.meta.url).href;
const NAT75073_PDF = new URL('../../assets/forms/nat75073-07-2022.pdf', import.meta.url).href;

export type OfficialFormCode = 'SA485' | 'SA457' | 'SA456';

export const OFFICIAL_FORMS: Record<OfficialFormCode, { template: FormTemplate; pdfUrl: string }> = {
    SA485: { template: sa485Template as unknown as FormTemplate, pdfUrl: SA485_PDF },
    SA457: { template: sa457Template as unknown as FormTemplate, pdfUrl: SA457_PDF },
    SA456: { template: sa456Template as unknown as FormTemplate, pdfUrl: SA456_PDF },
};

const dollars = (v: number): string => Math.round(v).toLocaleString('en-AU');
const upper = (s: string): string => s.toUpperCase();

/** The SA485's answers, from the point of view of the person the assessment is for. */
export function sa485Values(ff: DraftFactFind, who: Who, facts: PlanFacts = {}): FormValues {
    const v: FormValues = {};
    const p = personOf(ff, who);
    const couple = isCouple(ff);
    const partner = personOf(ff, otherOf(who));
    const c = completerOf(ff);
    const home = ff?.home || {};
    const protectedPerson = String(home.protectedPerson || 'no');
    const partnerStays = couple && protectedPerson === 'partner';

    v.q1_residential = true;
    v.q2_yes = true; // the form's whole premise; the chooser offers the SA457 otherwise
    if (ownsHome(ff)) v.q3_yes = true;
    if (c.onBehalf) {
        v.q4_yes = true;
        v.q4_name = upper(c.name);
        v.q4_relationship = upper(c.relationship);
    } else {
        v.q4_no = true;
    }
    if (couple) v.q5_yes = true; else v.q5_no = true;
    if (!couple) v.q6_na = true; // a couple ticks "Yes" themselves, once the partner has been told

    v.q8_you_family = upper(p.last);
    v.q8_you_first = upper(p.first);
    v.q9_you_dob = p.dob;
    if (couple) {
        v.q8_partner_family = upper(partner.last);
        v.q8_partner_first = upper(partner.first);
        v.q9_partner_dob = partner.dob;
    }

    if (p.residency !== 'aged_care') v.q12_option1 = true; // already in care: option 2 needs the entry date
    if (couple) { if (partnerStays) v.q13_yes = true; else v.q13_no = true; }

    if (!ownsHome(ff)) return v;
    v.q16_yes = true;
    v.q17_yes = true;
    v.q19_no = true;

    const s = sharesFor(home.ownership, who, couple);
    const inVillage = ff?.client?.currentResidency === 'retirement_village';
    if (inVillage) {
        v.q20_yes = true;
        v.q20_type = 'RETIREMENT VILLAGE UNIT';
        const entry = num(ff?.retirementVillage?.entryContribution) || num(home.value);
        if (entry > 0) v.q20_value = dollars(entry);
        v.q20_share_you = String(s.you);
        if (couple) v.q20_share_partner = String(s.partner);
        if (couple && partnerStays) v.q20_partner_yes = true; else v.q20_partner_no = true;
    } else {
        v.q20_no = true;
        const loan = homeLoanBalance(ff);
        const large = !!home.landSize && num(home.landSizeValue) > 0;
        const o = large ? 'q22_o2' : 'q22_o1';
        v[`${o}_select`] = true;
        v[`${o}_value`] = dollars(large ? Math.max(0, num(home.value) - num(home.landSizeValue)) : num(home.value));
        if (large) v.q22_o2_remaining = dollars(num(home.landSizeValue));
        v[`${o}_loan`] = dollars(loan);
        if (!s.entity) {
            v[`${o}_share_you`] = String(s.you);
            if (couple) v[`${o}_share_partner`] = String(s.partner);
            if (s.other) v[`${o}_share_other`] = String(s.other);
        }
        if (partnerStays) v[`${o}_partner_yes`] = true; else v[`${o}_partner_no`] = true;
    }

    if (home.businessUse) {
        v.q26_yes = true;
        if (num(home.businessUseValue) > 0) v.q26_value = dollars(num(home.businessUseValue));
    } else {
        v.q26_no = true;
        v.q27_no = true;
    }
    if (home.primaryProductionUse) v.q28_yes = true; else v.q28_no = true;
    // Let at the application date: only under a plan that rents the home out (planPosition.ts).
    if (facts.homeRented) v.q30_yes = true; else v.q30_no = true;
    if (protectedPerson === 'carer') v.q31_carer = true;
    else if (protectedPerson === 'close_relative') v.q31_relative = true;
    else v.q31_none = true;
    return v;
}

/* ─── SA457 and SA456: every income and asset ──────────────────────────────────────────────────── */

/** The opening questions the SA457 and SA456 share (same numbers on both): on behalf, partner, names, dates of birth, gender, contact. */
function aboutYouValues(v: FormValues, ff: DraftFactFind, who: Who) {
    const p = personOf(ff, who);
    const couple = isCouple(ff);
    const partner = personOf(ff, otherOf(who));
    const c = completerOf(ff);
    if (c.onBehalf) { v.q3_yes = true; v.q3_name = upper(c.name); v.q3_relationship = upper(c.relationship); } else v.q3_no = true;
    if (couple) v.q4_yes = true; else v.q4_no = true;
    if (!couple) v.q5_na = true;
    const person = (side: 'you' | 'partner', x: ReturnType<typeof personOf>) => {
        v[`q7_${side}_family`] = upper(x.last);
        v[`q7_${side}_first`] = upper(x.first);
        v[`q8_${side}_dob`] = x.dob;
        if (x.sex === 'male') v[`q9_${side}_male`] = true;
        if (x.sex === 'female') v[`q9_${side}_female`] = true;
        if (x.phone) v[`q11_${side}_phone`] = x.phone.replace(/\s+/g, '');
        if (x.email) v[`q11_${side}_email`] = x.email;
    };
    person('you', p);
    if (couple) person('partner', partner);
}

/** Ownership shares, said from the form-holder's side, into a "you" / "partner" pair of % boxes. */
function shareValues(v: FormValues, key: string, ownership: unknown, who: Who, couple: boolean) {
    const s = sharesFor(ownership, who, couple);
    if (s.entity) return;
    v[`${key}_share_you`] = String(s.you);
    if (couple) v[`${key}_share_partner`] = String(s.partner);
}

interface IncomeAssetNums {
    bank: { q: string; slots: number };
    super: { q: string; slots: number };
    managedNo: string;
    shares: { q: string; slots: number };
    bondsNo: string;
    funeral: { q: string; slots: number };
    lifeIns: { q: string; slots: number };
    gifts: string;
    overseas: string;
    business: string;
    trust: string;
    company: string;
    otherAssets: { q: string; slots: number };
    otherIncome: { q: string; slots: number };
    /** Money on loan to someone else (SA457 Q58, SA456 Q46). */
    loans: { q: string; slots: number };
}

/** Bank accounts to other income, the same profile facts on both forms; entries beyond the form's slots go on a separate sheet. */
function incomeAssetValues(v: FormValues, ff: DraftFactFind, who: Who, n: IncomeAssetNums, asAt: Date) {
    const couple = isCouple(ff);
    const yesNo = (q: string, yes: boolean) => { if (yes) v[`${q}_yes`] = true; else v[`${q}_no`] = true; };

    assetsOf(ff, 'bankAccounts', 'termDeposits').slice(0, n.bank.slots).forEach((a, i) => {
        const k = `${n.bank.q}_${i + 1}`;
        v[`${k}_name`] = upper(a.description || (a.type === 'termDeposits' ? 'Term deposit' : 'Bank account'));
        v[`${k}_type`] = a.type === 'termDeposits' ? 'TERM DEPOSIT' : 'SAVINGS';
        v[`${k}_balance`] = dollars(num(a.value ?? a.balance));
        shareValues(v, k, a.ownership, who, couple);
    });

    const supers = assetsOf(ff, 'superannuation', 'accountBasedPensions', 'annuities');
    yesNo(n.super.q, supers.length > 0);
    supers.slice(0, n.super.slots).forEach((a, i) => {
        const k = `${n.super.q}_${i + 1}`;
        v[`${k}_institution`] = upper(a.description || ({ superannuation: 'Superannuation', accountBasedPensions: 'Account-based pension', annuities: 'Annuity' } as Record<string, string>)[String(a.type)] || 'Super');
        v[`${k}_balance`] = dollars(num(a.value));
        if (num(a.income) > 0) { v[`${k}_income`] = dollars(num(a.income)); v[`${k}_often`] = 'YEAR'; }
        shareValues(v, k, a.ownership ?? who, who, couple);
    });

    const shares = assetsOf(ff, 'shares');
    if (!shares.length) v[`${n.managedNo}_no`] = true; // the profile records shares and managed funds together
    yesNo(n.shares.q, shares.length > 0);
    shares.slice(0, n.shares.slots).forEach((a, i) => {
        const k = `${n.shares.q}_${i + 1}`;
        v[`${k}_company`] = upper(a.description || 'Share portfolio');
        shareValues(v, k, a.ownership, who, couple);
    });

    v[`${n.bondsNo}_no`] = true;

    // The question covers funeral bonds and funeral contracts already paid for alike.
    const funeral = assetsOf(ff, 'funeralBonds', 'prepaidFuneral');
    yesNo(n.funeral.q, funeral.length > 0);
    funeral.slice(0, n.funeral.slots).forEach((a, i) => {
        const k = `${n.funeral.q}_${i + 1}`;
        v[`${k}_company`] = upper(a.description || (a.type === 'prepaidFuneral' ? 'Prepaid funeral' : 'Funeral bond'));
        v[`${k}_value`] = dollars(num(a.value));
        shareValues(v, k, a.ownership ?? who, who, couple);
    });

    const bonds = assetsOf(ff, 'investmentBonds');
    yesNo(n.lifeIns.q, bonds.length > 0);
    bonds.slice(0, n.lifeIns.slots).forEach((a, i) => {
        const k = `${n.lifeIns.q}_${i + 1}`;
        v[`${k}_product`] = upper(a.description || 'Investment bond');
        shareValues(v, k, a.ownership, who, couple);
    });

    const gifts = giftsInLastFiveYears(ff, asAt);
    if (gifts.length) v[`${n.gifts}_yes`] = true;
    else if (ff?.giftsHistory?.confirmedNone) v[`${n.gifts}_no`] = true;

    const overseas = (ff?.income?.otherIncomes || []).filter((i) => i.type === 'overseasPension');
    yesNo(n.overseas, overseas.length > 0);
    if (overseas[0]) {
        v[`${n.overseas}_1_type`] = upper(overseas[0].description || 'Overseas pension');
        v[`${n.overseas}_1_amount`] = `${dollars(num(overseas[0].amount))} A YEAR`;
    }

    const structures = ff?.structures || [];
    yesNo(n.business, structures.some((s) => s.type === 'partnership'));
    yesNo(n.trust, structures.some((s) => s.type === 'family_trust' || s.type === 'unit_trust'));
    yesNo(n.company, structures.some((s) => s.type === 'private_company'));

    const other = assetsOf(ff, 'other');
    yesNo(n.otherAssets.q, other.length > 0);
    other.slice(0, n.otherAssets.slots).forEach((a, i) => {
        const k = `${n.otherAssets.q}_${i + 1}`;
        v[`${k}_description`] = upper(a.description || 'Other asset');
        v[`${k}_value`] = dollars(num(a.value));
        shareValues(v, k, a.ownership, who, couple);
    });

    const loans = assetsOf(ff, 'loansToOthers');
    yesNo(n.loans.q, loans.length > 0);
    loans.slice(0, n.loans.slots).forEach((a, i) => {
        const k = `${n.loans.q}_${i + 1}`;
        v[`${k}_who`] = upper(a.description || 'Loan');
        if (a.commencementDate) v[`${k}_date`] = String(a.commencementDate);
        if (num(a.purchasePrice) > 0) v[`${k}_amount`] = dollars(num(a.purchasePrice));
        v[`${k}_balance`] = dollars(num(a.value));
        shareValues(v, k, a.ownership, who, couple);
    });

    const incomes = (ff?.income?.otherIncomes || []).filter((i) => i.type === 'salary' || i.type === 'other' || i.type === 'boarders' || i.type === 'dvaWarWidow' || i.type === 'dvaDisability');
    yesNo(n.otherIncome.q, incomes.length > 0);
    incomes.slice(0, n.otherIncome.slots).forEach((inc, i) => {
        const k = `${n.otherIncome.q}_${i + 1}`;
        v[`${k}_type`] = upper(inc.description || ({ salary: 'Salary or wages', other: 'Other income', boarders: 'Board and lodging', dvaWarWidow: "DVA war widow(er)'s pension", dvaDisability: 'DVA disability pension' } as Record<string, string>)[String(inc.type)] || 'Income');
        v[`${k}_gross`] = `${dollars(num(inc.amount))} per YEAR`;
    });
}

/** The SA457's answers: everything, as at the application date. */
export function sa457Values(ff: DraftFactFind, who: Who, asAt = new Date(), facts: PlanFacts = {}): FormValues {
    const v: FormValues = {};
    const p = personOf(ff, who);
    const couple = isCouple(ff);
    const home = ff?.home || {};
    const protectedPerson = String(home.protectedPerson || 'no');
    const partnerStays = couple && protectedPerson === 'partner';
    v.q1_residential = true;
    v.q2_no = true; // the SA457 is for someone NOT on a means-tested payment
    aboutYouValues(v, ff, who);
    v.q14_yes = true;
    if (p.residency !== 'aged_care') v.q15_option1 = true;
    if (couple) { if (partnerStays) v.q16_yes = true; else v.q16_no = true; }

    if (ownsHome(ff)) {
        v.q19_yes = true;
        v.q20_yes = true;
        v.q22_no = true;
        const s = sharesFor(home.ownership, who, couple);
        if (ff?.client?.currentResidency === 'retirement_village') {
            v.q23_yes = true;
            v.q23_type = 'RETIREMENT VILLAGE UNIT';
            const entry = num(ff?.retirementVillage?.entryContribution) || num(home.value);
            if (entry > 0) v.q23_value = dollars(entry);
            v.q23_share_you = String(s.you);
            if (couple) v.q23_share_partner = String(s.partner);
            if (partnerStays) v.q23_partner_yes = true; else v.q23_partner_no = true;
        } else {
            v.q23_no = true;
            const large = !!home.landSize && num(home.landSizeValue) > 0;
            const o = large ? 'q25_o2' : 'q25_o1';
            v[`${o}_select`] = true;
            v[`${o}_value`] = dollars(large ? Math.max(0, num(home.value) - num(home.landSizeValue)) : num(home.value));
            if (large) v.q25_o2_remaining = dollars(num(home.landSizeValue));
            v[`${o}_loan`] = dollars(homeLoanBalance(ff));
            if (!s.entity) {
                v[`${o}_share_you`] = String(s.you);
                if (couple) v[`${o}_share_partner`] = String(s.partner);
                if (s.other) v[`${o}_share_other`] = String(s.other);
            }
            if (partnerStays) v[`${o}_partner_yes`] = true; else v[`${o}_partner_no`] = true;
        }
        if (home.businessUse) { v.q29_yes = true; if (num(home.businessUseValue) > 0) v.q29_value = dollars(num(home.businessUseValue)); } else { v.q29_no = true; v.q30_no = true; }
        if (home.primaryProductionUse) v.q31_yes = true; else v.q31_no = true;
        // Rental income from the home at the entry date: under a plan that lets it (planPosition.ts).
        if (facts.homeRented) v.q33_yes = true; else v.q33_no = true;
        if (protectedPerson === 'carer') v.q34_carer = true;
        else if (protectedPerson === 'close_relative') v.q34_relative = true;
        else v.q34_none = true;
    } else {
        v.q19_no = true;
    }

    const ip = assetsOf(ff, 'investmentProperty')[0];
    if (ip) {
        v.q38_yes = true;
        const s = sharesFor(ip.ownership, who, couple);
        if (!s.entity) {
            if (s.you > 0) { v.q40_you = true; v.q40_share_you = String(s.you); }
            if (couple && s.partner > 0) { v.q40_partner = true; v.q40_share_partner = String(s.partner); }
        }
        v.q42_value = dollars(num(ip.value));
        if (loanBalanceFor(ff, ip.id) > 0) v.q45_yes = true; else v.q45_no = true;
        if (num(ip.rent) > 0) v.q46_yes = true; else v.q46_no = true;
    } else {
        v.q38_no = true;
    }

    incomeAssetValues(v, ff, who, {
        bank: { q: 'q50', slots: 2 }, super: { q: 'q51', slots: 2 }, managedNo: 'q52', shares: { q: 'q53', slots: 2 }, bondsNo: 'q54',
        funeral: { q: 'q55', slots: 2 }, lifeIns: { q: 'q56', slots: 2 }, gifts: 'q59', overseas: 'q60', business: 'q61', trust: 'q62', company: 'q64',
        otherAssets: { q: 'q65', slots: 2 }, otherIncome: { q: 'q66', slots: 2 }, loans: { q: 'q58', slots: 2 },
    }, asAt);
    const rad = assetsOf(ff, 'radPaid');
    if (rad.length) { v.q57_yes = true; v.q57_amount = dollars(rad.reduce((t, a) => t + num(a.value), 0)); } else v.q57_no = true;
    return v;
}

/** The SA456's answers: income and assets for Support at Home (the home itself is not counted). */
export function sa456Values(ff: DraftFactFind, who: Who, asAt = new Date()): FormValues {
    const v: FormValues = {};
    const couple = isCouple(ff);
    const home = ff?.home || {};
    v.q1_option1 = true;
    v.q2_no = true;
    aboutYouValues(v, ff, who);
    v.q15_yes = true;
    v.q17_no = true; // no part of the home let at the start date
    if (ownsHome(ff)) {
        v.q18_yes = true;
        if (home.businessUse || home.landSize || home.separateTitles) v.q20_yes = true; else v.q20_no = true;
    } else {
        v.q18_no = true;
        v.q19_no = true;
    }
    const ips = assetsOf(ff, 'investmentProperty');
    const ip = ips[0];
    if (ip) {
        v.q27_yes = true;
        v.q28_count = String(ips.length);
        const s = sharesFor(ip.ownership, who, couple);
        if (!s.entity) {
            if (s.you > 0) { v.q31_you = true; v.q31_share_you = String(s.you); }
            if (couple && s.partner > 0) { v.q31_partner = true; v.q31_share_partner = String(s.partner); }
        }
        if (num(ip.rent) > 0) v.q32_yes = true; else v.q32_no = true;
        v.q34_value = dollars(num(ip.value));
        if (loanBalanceFor(ff, ip.id) > 0) v.q37_yes = true; else v.q37_no = true;
    } else {
        v.q27_no = true;
    }
    incomeAssetValues(v, ff, who, {
        bank: { q: 'q39', slots: 4 }, super: { q: 'q40', slots: 2 }, managedNo: 'q41', shares: { q: 'q42', slots: 2 }, bondsNo: 'q43',
        funeral: { q: 'q44', slots: 2 }, lifeIns: { q: 'q45', slots: 2 }, gifts: 'q47', overseas: 'q48', business: 'q49', trust: 'q50', company: 'q52',
        otherAssets: { q: 'q56', slots: 2 }, otherIncome: { q: 'q53', slots: 2 }, loans: { q: 'q46', slots: 2 },
    }, asAt);
    return v;
}

export function officialFormValues(code: OfficialFormCode, ff: DraftFactFind, who: Who, facts: PlanFacts = {}): FormValues {
    switch (code) {
        case 'SA485': return sa485Values(ff, who, facts);
        case 'SA457': return sa457Values(ff, who, new Date(), facts);
        case 'SA456': return sa456Values(ff, who);
        default: return {};
    }
}

/**
 * The ATO's Downsizer contribution into super form (NAT 75073-07.2022): its own fillable fields.
 * Names, date of birth, contact details, the fund (where the profile names one) and the amount
 * from the saved plan; the TFN, member number, address and the signature are the client's.
 */
export function nat75073Values(ff: DraftFactFind, who: Who, amount: number): Record<string, string> {
    const p = personOf(ff, who);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(p.dob);
    const fund = assetsOf(ff, 'superannuation', 'accountBasedPensions').filter((a) => !a.ownership || a.ownership === who)[0];
    return {
        contactFamilyName: upper(p.last),
        contactGivenName: upper(p.first),
        ...(m ? { dateDay: m[3]!, dateMonth: m[2]!, dateYear: m[1]! } : {}),
        ...(fund?.description ? { 'super-fund-name-line1': upper(fund.description) } : {}),
        ...(p.phone ? { daytimePhone: p.phone.replace(/\s+/g, '') } : {}),
        ...(p.email ? { Email: p.email } : {}),
        ...(amount > 0 ? { contributionAmount: dollars(Math.min(amount, 300_000)) } : {}),
        'dec-1-name': upper(p.full),
    };
}

export async function openDownsizerForm(ff: DraftFactFind, who: Who, amount: number): Promise<void> {
    const bytes = await (await fetch(NAT75073_PDF)).arrayBuffer();
    const filled = await fillAcroForm(bytes, nat75073Values(ff, who, amount), { title: 'Downsizer contribution into superannuation — pre-filled', subject: 'NAT 75073-07.2022, pre-filled from the client profile. Source: ato.gov.au.' });
    openFilledPdf(filled, `NAT75073_${personOf(ff, who).first || 'Client'}_prefilled_${new Date().toISOString().slice(0, 10)}.pdf`);
}

/**
 * Fetch the official form, fill it from the profile, and hand it to the client. `ff` is whichever
 * position the client chose (planPosition.ts); a form filled from the position after a plan says so
 * in its file name, so it is not mistaken for one to lodge today.
 */
export async function openOfficialForm(code: OfficialFormCode, ff: DraftFactFind, who: Who, opts: { afterPlan?: boolean; facts?: PlanFacts } = {}): Promise<void> {
    const { template, pdfUrl } = OFFICIAL_FORMS[code];
    const bytes = await (await fetch(pdfUrl)).arrayBuffer();
    const filled = await fillOfficialForm(bytes, template, officialFormValues(code, ff, who, opts.facts));
    const name = personOf(ff, who).first || 'Client';
    openFilledPdf(filled, `${code}_${name}_${opts.afterPlan ? 'after-plan' : 'prefilled'}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
